import * as React from 'react';

/**
 * The curve the `Sidebar` primitive animates its own width on. Everything that
 * has to travel with the collapse — padding that re-centres icons in the 48px
 * rail, labels that fade out, panels that concertina shut — has to match it or
 * the collapse comes apart. It was retyped at thirteen call sites.
 */
const COLLAPSE_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Transition list for properties that travel with the collapse. Layout takes the
 * primitive's own 300ms; opacity resolves in half that, so a label has finished
 * fading before the rail finishes narrowing rather than smearing across it.
 */
export const collapseTransition = (...properties: string[]): string =>
  properties
    .map(
      (property) =>
        `${property} ${property === 'opacity' ? 150 : 300}ms ${COLLAPSE_EASE}`
    )
    .join(', ');

/** A week, matching the lifetime the `Sidebar` primitive gives its own cookie. */
const SECTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

function readSectionOpen(cookieName: string): boolean {
  if (typeof document === 'undefined') {
    return true;
  }
  const entry = document.cookie
    .split('; ')
    .find((part) => part.startsWith(`${cookieName}=`));
  // Open unless it was explicitly shut: these sections are the sidebar's main
  // content, and someone who has never touched the disclosure shouldn't have to
  // go looking for it.
  return entry === undefined
    ? true
    : entry.slice(cookieName.length + 1) !== 'false';
}

/**
 * Open/closed state for one collapsible sidebar section, persisted in a cookie
 * on the same path and lifetime the `Sidebar` primitive uses for its own
 * collapse — one mechanism for one kind of preference, rather than a second
 * store alongside it.
 *
 * Shared by Favorites and Recents. It was written out twice, once per section,
 * differing only in the cookie's name; a third section would have copied it
 * again.
 */
export function useSidebarSectionOpen(
  cookieName: string
): [boolean, (next: boolean) => void] {
  const [open, setOpen] = React.useState(() => readSectionOpen(cookieName));
  const setSectionOpen = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      document.cookie = `${cookieName}=${next}; path=/; max-age=${SECTION_COOKIE_MAX_AGE}`;
    },
    [cookieName]
  );
  return [open, setSectionOpen];
}
